import React, { useMemo } from 'react';
import Layout from '../components/Layout';
import useEvaluation from '../hooks/useEvaluation';
import useEnseignantData from '../hooks/useEnseignantData';
import EvaluationHeader from '../components/evaluations/EvaluationHeader';
import EvaluationFilters from '../components/evaluations/EvaluationFilters';
import EvaluationTable from '../components/evaluations/EvaluationTable';
import EvaluationForm from '../components/evaluations/EvaluationForm';
import StatistiquesEvaluations from '../components/evaluations/StatistiquesEvaluations';
import ConfirmationModal from '../components/ConfirmationModal';

const EvaluationPage = () => {
  // Hook pour gérer les données spécifiques aux enseignants
  const { resetEnseignantData } = useEnseignantData();

  // Hook principal pour la gestion des évaluations
  const {
    // States
    activeTab,
    showModal,
    showDeleteModal,
    itemToDelete,
    isDeleting,
    isEditing,
    controles,
    loading,
    classes,
    modules,
    etudiants,
    selectedClasse,
    selectedModule,
    currentPage,
    totalPages,
    showFilters,
    filters,
    formData,
    selectedControle,
    
    // Setters
    setActiveTab,
    setShowModal,
    setShowDeleteModal,
    setSelectedClasse,
    setSelectedModule,
    setCurrentPage,
    setShowFilters,
    setFilters,
    setFormData,
    setSelectedControle,
    
    // Actions
    handleSubmit,
    handleDelete,
    confirmDelete,
    handleEdit,
    formatDate,
    formatTime,
    resetForm,
    loadModulesByClasse,
    loadEtudiantsByClasse
  } = useEvaluation();

  // Fonction pour changer d'onglet avec reset des données
  const handleTabChange = (newTab) => {
    if (newTab !== activeTab) {
      resetEnseignantData();
      resetForm();
    }
    setActiveTab(newTab);
  };

  // Optimisation du rendu avec useMemo
  const currentData = useMemo(() => {
    return controles || [];
  }, [controles]);

  // Vérification si on doit afficher l'état vide
  const showEmptyState = useMemo(() => {
    return controles.length === 0;
  }, [controles.length]);

  // Vérification si on doit afficher le tableau
  const showTable = useMemo(() => {
    return controles.length > 0;
  }, [controles.length]);

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
            <span className="ml-3 text-gray-600">Chargement des évaluations...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          {/* Card principale avec design professionnel */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Header avec onglets et statistiques */}
            <EvaluationHeader
              setShowFilters={setShowFilters}
              showFilters={showFilters}
              setShowModal={setShowModal}
            />

            {/* Filtres */}
            {showFilters && (
              <EvaluationFilters
                filters={filters}
                setFilters={setFilters}
                classes={classes}
                modules={modules}
                selectedClasse={selectedClasse}
                setSelectedClasse={setSelectedClasse}
                selectedModule={selectedModule}
                setSelectedModule={setSelectedModule}
                loadModulesByClasse={loadModulesByClasse}
              />
            )}

            {/* État vide */}
            {showEmptyState && (
              <div className="p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="p-4 bg-gray-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <svg className="h-8 w-8 text-[#202654]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Aucune évaluation enregistrée
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Commencez par créer un contrôle ou une évaluation.
                  </p>
                  <button
                    onClick={() => setShowModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#202654] hover:bg-[#202654]/90"
                  >
                    <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Nouvelle Évaluation
                  </button>
                </div>
              </div>
            )}

            {/* Tableau des évaluations */}
            {showTable && (
                          <EvaluationTable
              controles={controles}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
              formatDate={formatDate}
              formatTime={formatTime}
            />
            )}

            {/* Pagination conditionnelle */}
            {totalPages > 1 && (
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Page <span className="font-medium">{currentPage}</span> sur{' '}
                    <span className="font-medium">{totalPages}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Précédent
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Statistiques détaillées */}
          <StatistiquesEvaluations controles={controles} />
        </div>

        {/* Formulaire d'ajout/modification */}
        <EvaluationForm
          showModal={showModal}
          setShowModal={setShowModal}
          formData={formData}
          setFormData={setFormData}
          selectedClasse={selectedClasse}
          setSelectedClasse={setSelectedClasse}
          selectedModule={selectedModule}
          setSelectedModule={setSelectedModule}
          classes={classes}
          modules={modules}
          handleSubmit={handleSubmit}
          resetForm={resetForm}
          isEditing={isEditing}
          loadModulesByClasse={loadModulesByClasse}
        />



        {/* Modal de confirmation pour la suppression */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
          }}
          onConfirm={confirmDelete}
          title="Supprimer l'évaluation"
          message="Êtes-vous sûr de vouloir supprimer cette évaluation ? Cette action supprimera également toutes les notes associées."
          confirmText="Supprimer"
          cancelText="Annuler"
          type="danger"
          isLoading={isDeleting}
        />
      </div>
    </Layout>
  );
};

export default EvaluationPage;
